import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, mutate } from '../shared/api';

type Locale = 'hr' | 'en';
type Comment = { id: number; body: string; author: string | null; created_at: string | null };
type Task = {
    id: number; task: string; prio: string | null; rok: string | null; status_note: string | null;
    pct_complete: number; notes: string | null; done_at: string | null; comments: Comment[];
};
type Section = { section: string; tasks: Task[]; done: number; total: number; avg_pct: number };
type Payload = { sections: Section[]; overall: { total: number; done: number; avg_pct: number } };

const prioColor = (p: string | null) => {
    const s = (p || '').toUpperCase();
    if (s.includes('P1')) return '#b14040';
    if (s.includes('P2')) return 'var(--ad-acc-3)';
    return '#6b7280';
};
const pctColor = (n: number) => (n >= 100 ? '#1f7a36' : n >= 50 ? 'var(--ad-acc-3)' : n > 0 ? 'var(--ad-acc)' : '#c9c2ad');

function Bar({ pct, h = 8 }: { pct: number; h?: number }) {
    return (
        <div style={{ background: 'rgba(var(--ad-soft-rgb),0.25)', borderRadius: 6, height: h, overflow: 'hidden', flex: 1 }}>
            <div style={{ width: `${Math.min(100, Math.max(0, pct))}%`, height: '100%', background: pctColor(pct), transition: 'width .2s' }} />
        </div>
    );
}

export function CfoTasksModule({ token, locale, onBack }: { token: string; locale: Locale; onBack: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<Payload | null>(null);
    const [err, setErr] = useState('');
    const [savingId, setSavingId] = useState<number | null>(null);
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
    const [draftComment, setDraftComment] = useState<Record<number, string>>({});
    const debounce = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

    const load = useCallback(() => {
        api<Payload>('/cfo-tasks', token).then(setData).catch(() => setErr('Greška pri učitavanju.'));
    }, [token]);
    useEffect(() => { load(); }, [load]);

    const patchLocal = (id: number, fields: Partial<Task>) => {
        setData((d) => d && ({
            ...d,
            sections: d.sections.map((s) => ({
                ...s,
                tasks: s.tasks.map((x) => (x.id === id ? { ...x, ...fields } : x)),
                avg_pct: Math.round(s.tasks.reduce((a, x) => a + (x.id === id && fields.pct_complete != null ? fields.pct_complete : x.pct_complete), 0) / s.tasks.length),
                done: s.tasks.filter((x) => (x.id === id && fields.pct_complete != null ? fields.pct_complete : x.pct_complete) >= 100).length,
            })),
        }));
    };

    const savePct = (id: number, pct: number) => {
        patchLocal(id, { pct_complete: pct, done_at: pct >= 100 ? new Date().toISOString() : null });
        clearTimeout(debounce.current[id]);
        debounce.current[id] = setTimeout(async () => {
            setSavingId(id);
            try { await mutate(`/cfo-tasks/${id}`, token, 'PATCH', { pct_complete: pct }); } catch { /* */ }
            setSavingId(null);
        }, 400);
    };
    const saveNotes = async (id: number, notes: string) => {
        setSavingId(id);
        try { await mutate(`/cfo-tasks/${id}`, token, 'PATCH', { notes }); } catch { /* */ }
        setSavingId(null);
    };
    const addComment = async (id: number) => {
        const body = (draftComment[id] || '').trim();
        if (!body) return;
        try {
            await mutate(`/cfo-tasks/${id}/comments`, token, 'POST', { body });
            setDraftComment((d) => ({ ...d, [id]: '' }));
            load();
        } catch { /* */ }
    };
    const delComment = async (cid: number) => {
        if (!confirm(t('Obrisati komentar?', 'Delete comment?'))) return;
        try { await mutate(`/cfo-tasks/comments/${cid}`, token, 'DELETE'); load(); } catch { /* */ }
    };

    const fmt = (s: string | null) => {
        if (!s) return '';
        const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
        return m ? `${m[3]}.${m[2]}.${m[1]}. ${m[4]}:${m[5]}` : s;
    };

    return (
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '8px 4px 60px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                <button type="button" className="secondary compact" onClick={onBack} style={{ fontSize: '0.8rem' }}>← {t('Natrag', 'Back')}</button>
                <h2 style={{ margin: 0, color: 'var(--ad-ink)' }}>🎯 {t('CFO Radni popis 2026', 'CFO Worklist 2026')}</h2>
            </div>
            <p className="muted" style={{ marginTop: 0, fontSize: '0.84rem' }}>
                {t('Osobni tracker. Postavi % izvršenosti, piši bilješke i komentare. 100% = gotovo.', 'Personal tracker. Set % complete, write notes and comments. 100% = done.')}
            </p>

            {err ? <p className="error">{err}</p> : null}
            {!data ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : (
                <>
                    {/* Ukupni napredak */}
                    <div style={{ background: 'var(--ad-bg)', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
                            <strong style={{ fontSize: '1.4rem', color: pctColor(data.overall.avg_pct) }}>{data.overall.avg_pct}%</strong>
                            <span className="muted" style={{ fontSize: '0.84rem' }}>
                                {t('ukupno', 'overall')} · {data.overall.done}/{data.overall.total} {t('zadataka gotovo', 'tasks done')}
                            </span>
                        </div>
                        <Bar pct={data.overall.avg_pct} h={12} />
                    </div>

                    {data.sections.map((s) => {
                        const open = !collapsed[s.section];
                        return (
                            <div key={s.section} style={{ marginBottom: 12, border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 10, overflow: 'hidden' }}>
                                <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [s.section]: !c[s.section] }))}
                                    style={{ width: '100%', textAlign: 'left', background: 'var(--ad-bg)', border: 0, cursor: 'pointer', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <span style={{ fontSize: '0.9rem', color: '#888' }}>{open ? '▾' : '▸'}</span>
                                    <strong style={{ flex: 1, color: 'var(--ad-ink)', fontSize: '0.9rem' }}>{s.section}</strong>
                                    <span className="muted" style={{ fontSize: '0.76rem', whiteSpace: 'nowrap' }}>{s.done}/{s.total} · {s.avg_pct}%</span>
                                    <div style={{ width: 120 }}><Bar pct={s.avg_pct} /></div>
                                </button>

                                {open ? (
                                    <div style={{ padding: '4px 10px 10px' }}>
                                        {s.tasks.map((task) => {
                                            const done = task.pct_complete >= 100;
                                            return (
                                                <div key={task.id} style={{ borderTop: '1px solid rgba(var(--ad-soft-rgb),0.18)', padding: '10px 4px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                                        {task.prio ? <span style={{ background: prioColor(task.prio), color: '#fff', padding: '1px 7px', borderRadius: 10, fontSize: '0.68rem', fontWeight: 800, whiteSpace: 'nowrap', marginTop: 2 }}>{task.prio}</span> : null}
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 600, color: 'var(--ad-ink)', fontSize: '0.9rem', textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.6 : 1 }}>{task.task}</div>
                                                            <div className="muted" style={{ fontSize: '0.74rem', marginTop: 1 }}>
                                                                {task.rok ? <>📅 {task.rok}{'  ·  '}</> : null}{task.status_note}
                                                            </div>
                                                        </div>
                                                        {done ? <span style={{ background: 'rgba(31,122,54,0.14)', color: '#1f7a36', padding: '2px 9px', borderRadius: 10, fontSize: '0.72rem', fontWeight: 800, whiteSpace: 'nowrap' }}>✓ {t('GOTOVO', 'DONE')}</span> : null}
                                                    </div>

                                                    {/* % izvršenosti */}
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                                                        <input type="range" min={0} max={100} step={5} value={task.pct_complete}
                                                            onChange={(e) => savePct(task.id, parseInt(e.target.value))} style={{ flex: 1, accentColor: pctColor(task.pct_complete) as string }} />
                                                        <strong style={{ width: 44, textAlign: 'right', color: pctColor(task.pct_complete), fontSize: '0.86rem' }}>{task.pct_complete}%</strong>
                                                        <div style={{ display: 'flex', gap: 3 }}>
                                                            {[0, 25, 50, 75, 100].map((v) => (
                                                                <button key={v} type="button" onClick={() => savePct(task.id, v)}
                                                                    style={{ fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4, cursor: 'pointer', border: '1px solid rgba(var(--ad-soft-rgb),0.4)', background: task.pct_complete === v ? pctColor(v) : '#fff', color: task.pct_complete === v ? '#fff' : '#666' }}>{v}</button>
                                                            ))}
                                                        </div>
                                                        {savingId === task.id ? <span className="muted" style={{ fontSize: '0.68rem' }}>💾</span> : null}
                                                    </div>

                                                    {/* Bilješke */}
                                                    <textarea defaultValue={task.notes ?? ''} placeholder={t('Bilješke…', 'Notes…')}
                                                        onBlur={(e) => { if (e.target.value !== (task.notes ?? '')) void saveNotes(task.id, e.target.value); }}
                                                        style={{ width: '100%', marginTop: 8, minHeight: 40, fontSize: '0.82rem', padding: '6px 8px', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 6, resize: 'vertical', boxSizing: 'border-box' }} />

                                                    {/* Komentari */}
                                                    {task.comments.length > 0 ? (
                                                        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                            {task.comments.map((c) => (
                                                                <div key={c.id} style={{ background: 'var(--ad-bg)', borderRadius: 6, padding: '4px 8px', fontSize: '0.8rem', display: 'flex', gap: 8, alignItems: 'baseline' }}>
                                                                    <span style={{ flex: 1, color: '#444' }}>{c.body}</span>
                                                                    <span className="muted" style={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>{fmt(c.created_at)}</span>
                                                                    <button type="button" onClick={() => delComment(c.id)} title={t('Obriši', 'Delete')} style={{ border: 0, background: 'transparent', color: '#b14040', cursor: 'pointer', fontSize: '0.72rem' }}>✕</button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : null}
                                                    <div style={{ display: 'flex', gap: 6, marginTop: 5 }}>
                                                        <input type="text" value={draftComment[task.id] ?? ''} placeholder={t('Dodaj komentar…', 'Add comment…')}
                                                            onChange={(e) => setDraftComment((d) => ({ ...d, [task.id]: e.target.value }))}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') void addComment(task.id); }}
                                                            style={{ flex: 1, fontSize: '0.8rem', padding: '5px 8px', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 6 }} />
                                                        <button type="button" className="secondary compact" onClick={() => void addComment(task.id)} style={{ fontSize: '0.76rem' }}>💬 {t('Dodaj', 'Add')}</button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </>
            )}
        </div>
    );
}
